import { Table2 } from '@/components/icons';
import { useSidebar } from '@/features/app/contexts/SidebarContext';
import { useHookPermission } from '@/sdk/hooks/use-hook-permission';
import type { Table } from '@/sdk/model';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@/ui-lib/shadcn';
import { Input } from '@/ui-lib/shadcn/ui/input';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import useClickAway from 'react-use/lib/useClickAway';
import { Emoji } from '../../components/emoji/Emoji';

interface IProps {
  table: Table;
  isActive: boolean;
  className?: string;
  open?: boolean;
  isDragging?: boolean;
}

export const TableListItem: React.FC<IProps> = ({ table, isActive, className, isDragging }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const viewId = search.viewId;
  const permission = useHookPermission();
  const { leftVisible } = useSidebar();
  const isCollapsed = leftVisible === 'collapsed';

  const navigateHandler = () => {
    navigate({
      to: '/table/$tableId/$viewId',
      params: {
        tableId: table.id,
        viewId: table.defaultViewId,
      },
      replace: Boolean(table.defaultViewId) && Boolean(viewId),
    });
  };

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => inputRef.current?.focus());
    }
  }, [isEditing]);

  useClickAway(inputRef, () => {
    if (isEditing && inputRef.current?.value && inputRef.current.value !== table.name) {
      table.updateName(inputRef.current.value);
    }
    setIsEditing(false);
  });

  const buttonContent = (
    <Button
      variant={'ghost'}
      size={'xs'}
      asChild
      className={cn(
        'my-[2px] w-full text-sm font-normal gap-2 group bg-popover',
        isCollapsed ? 'px-2 justify-center' : 'px-2 justify-start',
        className,
        {
          'bg-secondary/90': isActive,
        }
      )}
      onClick={navigateHandler}
    >
      <div className={cn('flex items-center', isCollapsed && 'justify-center')}>
        <div>
          {table.icon ? (
            <Emoji emoji={table.icon} size={'1rem'} />
          ) : (
            <Table2 className="size-4 shrink-0" />
          )}
        </div>
        {!isCollapsed && (
          <p
            className="grow truncate"
            onDoubleClick={() => {
              permission['table|manage'] && setIsEditing(true);
            }}
          >
            {' ' + table.name}
          </p>
        )}
      </div>
    </Button>
  );

  return (
    <>
      {isCollapsed ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
            <TooltipContent>
              <p>{table.name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        buttonContent
      )}
      {isEditing && (
        <Input
          ref={inputRef}
          type="text"
          placeholder="name"
          defaultValue={table.name}
          style={{
            boxShadow: 'none',
          }}
          className="round-none absolute left-0 top-0 size-full cursor-text bg-background px-4 outline-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (e.currentTarget.value && e.currentTarget.value !== table.name) {
                table.updateName(e.currentTarget.value);
              }
              setIsEditing(false);
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        />
      )}
    </>
  );
};
